import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { useSessao } from '../lib/sessao'
import './receber-atendimento.css'

/**
 * Receber pelo atendimento, de dentro do cartão da agenda.
 *
 * ─── POR QUE ESTE É O ITEM MAIS ESTRUTURANTE DO DINHEIRO ──────────
 *
 * Até aqui, o ÚNICO lugar do produto que insere em `public.recebivel`
 * era `POST /v1/recebiveis` avulso, sem relação com o atendimento.
 * Concluir uma consulta não tocava em dinheiro: a recepção redigitava
 * paciente, descrição, valor e vencimento — que o agendamento já tem —
 * vinte vezes por dia.
 *
 * E, pior, silenciosamente: como nenhum recebível nascia com
 * `agendamento_id`, a `comissao_profissional.comissao_liquidada_centavos`
 * — que soma comissão de recebível liquidado E amarrado ao agendamento —
 * era ZERO permanente. O repasse mostrava R$ 0,00 para todo mundo, e
 * `app.pagar_repasse` recusava pagar acima do liberado. Cobrar por aqui
 * preenche o `agendamento_id`, e é isso que destrava o repasse.
 *
 * ─── COBRAR DUAS VEZES ────────────────────────────────────────────
 *
 * Não há `unique` em `recebivel.agendamento_id` — de propósito, porque
 * um atendimento pode gerar mais de uma cobrança (o procedimento e um
 * material à parte). Então a tela é que evita a duplicata boba: lê o
 * que JÁ existe para este atendimento antes de oferecer "Receber", e
 * mostra o que foi cobrado em vez de um botão que gera a segunda linha.
 */

/** "Recebido" são DOIS status: Pix/dinheiro/cortesia liquidam na hora
   (LIQUIDADO); cartão e boleto ficam em PAGO até cair. */
const RECEBIDO = new Set(['PAGO', 'LIQUIDADO'])

const MEIOS = [
  { id: 'PIX',            rotulo: 'Pix' },
  { id: 'DINHEIRO',       rotulo: 'Dinheiro' },
  { id: 'CARTAO_CREDITO', rotulo: 'Crédito' },
  { id: 'CARTAO_DEBITO',  rotulo: 'Débito' },
  { id: 'CORTESIA',       rotulo: 'Cortesia' },
] as const

type Recebivel = {
  id: string; descricao: string; bruto_centavos: string
  status: string; meio: string | null; agendamento_id: string | null
}

const brl = (c: string | number) =>
  (Number(c) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function ReceberAtendimento({ agendamentoId, pacienteId, valorSugerido, descricao }: {
  agendamentoId: string
  pacienteId: string | null
  valorSugerido: string | null
  descricao: string
}) {
  const { pode } = useSessao()
  const podeReceber = pode('financeiro:write')

  const [carregando, setCarregando] = useState(true)
  const [itens, setItens] = useState<Recebivel[]>([])
  const [abrindo, setAbrindo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    try {
      const r = await api<{ itens: Recebivel[] }>(
        `/v1/recebiveis?agendamento_id=${agendamentoId}&limite=20`)
      setItens(r.itens)
    } catch {
      setItens([])
    } finally { setCarregando(false) }
  }, [agendamentoId])
  useEffect(() => { void carregar() }, [carregar])

  if (carregando) return null

  const recebidos = itens.filter((r) => RECEBIDO.has(r.status))
  const pendentes = itens.filter((r) => !RECEBIDO.has(r.status)
                                     && r.status !== 'CANCELADO' && r.status !== 'ESTORNADO')

  return (
    <section className="rec-at">
      <h3 className="rec-at__titulo">Dinheiro</h3>
      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      {/* O que já aconteceu com o dinheiro deste atendimento. */}
      {recebidos.map((r) => (
        <div key={r.id} className="rec-at__linha is-recebido">
          <span className="rec-at__ok" aria-hidden="true">✓</span>
          <div>
            <strong>{brl(r.bruto_centavos)} recebido</strong>
            <span className="ajuda">{r.descricao}{r.meio && ` · ${rotuloMeio(r.meio)}`}</span>
          </div>
        </div>
      ))}

      {pendentes.map((r) => (
        <DarBaixa key={r.id} recebivel={r} podeReceber={podeReceber}
                  aoBaixar={carregar} aoErro={setErro} />
      ))}

      {/* Só oferece cobrar se não há nada aberto nem recebido — a
          segunda cobrança do mesmo atendimento é decisão, não engano. */}
      {itens.length === 0 && (
        podeReceber
          ? (pacienteId
              ? (abrindo
                  ? <FormReceber
                      agendamentoId={agendamentoId} pacienteId={pacienteId}
                      valorSugerido={valorSugerido} descricao={descricao}
                      aoFechar={() => setAbrindo(false)}
                      aoPronto={() => { setAbrindo(false); void carregar() }}
                      aoErro={setErro} />
                  : <button className="botao botao--pequeno rec-at__cobrar"
                            onClick={() => setAbrindo(true)}>Receber</button>)
              : <p className="ajuda">Marque um paciente para poder cobrar.</p>)
          : <p className="ajuda">Nada cobrado ainda.</p>
      )}
    </section>
  )
}

const ROTULO_MEIO: Record<string, string> = {
  PIX: 'Pix', DINHEIRO: 'Dinheiro', CARTAO_CREDITO: 'Crédito',
  CARTAO_DEBITO: 'Débito', BOLETO: 'Boleto', TRANSFERENCIA: 'Transferência',
  CREDITO_PACIENTE: 'Crédito do paciente', CORTESIA: 'Cortesia',
}
const rotuloMeio = (m: string) => ROTULO_MEIO[m] ?? m

function FormReceber({ agendamentoId, pacienteId, valorSugerido, descricao, aoFechar, aoPronto, aoErro }: {
  agendamentoId: string
  pacienteId: string
  valorSugerido: string | null
  descricao: string
  aoFechar(): void
  aoPronto(): void
  aoErro(m: string | null): void
}) {
  const [valor, setValor] = useState(
    valorSugerido ? (Number(valorSugerido) / 100).toFixed(2).replace('.', ',') : '')
  const [meio, setMeio] = useState('PIX')
  const [salvando, setSalvando] = useState(false)

  const centavos = Math.round(Number(valor.replace(/\./g, '').replace(',', '.') || 0) * 100)
  const hoje = new Date().toISOString().slice(0, 10)

  /** Cria o recebível JÁ com o agendamento_id — o campo que destrava o
     repasse. Devolve o id (a rota responde `{ids:[...]}`, plural). */
  async function criar(): Promise<string | null> {
    const r = await api<{ ids: string[] }>('/v1/recebiveis', {
      metodo: 'POST',
      corpo: {
        paciente_id: pacienteId,
        agendamento_id: agendamentoId,
        descricao: descricao || 'Atendimento',
        bruto_centavos: centavos,
        competencia: hoje,
        vencimento: hoje,
      },
    })
    return r.ids?.[0] ?? null
  }

  async function receberAgora(e: React.FormEvent) {
    e.preventDefault()
    if (centavos <= 0) { aoErro('Informe o valor.'); return }
    setSalvando(true); aoErro(null)
    try {
      const id = await criar()
      if (id) await api(`/v1/recebiveis/${id}/baixa`, { metodo: 'POST', corpo: { meio } })
      aoPronto()
    } catch (err) {
      aoErro(mensagemErro(err, meio))
      setSalvando(false)
    }
  }

  async function cobrarDepois() {
    if (centavos <= 0) { aoErro('Informe o valor.'); return }
    setSalvando(true); aoErro(null)
    try {
      await criar()
      aoPronto()
    } catch (err) {
      aoErro(err instanceof ErroApi ? err.message : 'Não foi possível registrar.')
      setSalvando(false)
    }
  }

  return (
    <form className="rec-at__form" onSubmit={receberAgora}>
      <div className="rec-at__valor">
        <label htmlFor="rec-valor">Valor</label>
        <input id="rec-valor" className="entrada" inputMode="decimal" autoFocus
               placeholder="0,00" value={valor}
               onChange={(e) => setValor(e.target.value)} />
      </div>

      <div className="rec-at__meios" role="group" aria-label="Forma de pagamento">
        {MEIOS.map((m) => (
          <button key={m.id} type="button"
                  className={`rec-at__meio${meio === m.id ? ' is-ativo' : ''}`}
                  aria-pressed={meio === m.id}
                  onClick={() => setMeio(m.id)}>{m.rotulo}</button>
        ))}
      </div>

      <div className="rec-at__acoes">
        <button type="button" className="botao botao--fantasma botao--pequeno"
                disabled={salvando} onClick={cobrarDepois}>Cobrar depois</button>
        <button type="submit" className="botao botao--pequeno" disabled={salvando}>
          {salvando ? 'Recebendo…' : 'Receber agora'}
        </button>
      </div>
      <button type="button" className="rec-at__cancelar" onClick={aoFechar}>Cancelar</button>
    </form>
  )
}

/** Recebível já criado, ainda aberto: dar baixa escolhendo o meio. */
function DarBaixa({ recebivel, podeReceber, aoBaixar, aoErro }: {
  recebivel: Recebivel
  podeReceber: boolean
  aoBaixar(): void | Promise<void>
  aoErro(m: string | null): void
}) {
  const [escolhendo, setEscolhendo] = useState(false)
  const [salvando, setSalvando] = useState(false)

  async function baixar(meio: string) {
    setSalvando(true); aoErro(null)
    try {
      await api(`/v1/recebiveis/${recebivel.id}/baixa`, { metodo: 'POST', corpo: { meio } })
      await aoBaixar()
    } catch (err) {
      aoErro(mensagemErro(err, meio))
      setSalvando(false)
    }
  }

  return (
    <div className="rec-at__linha is-pendente">
      <span className="rec-at__aguardando" aria-hidden="true">•</span>
      <div className="rec-at__pend-txt">
        <strong>{brl(recebivel.bruto_centavos)} a receber</strong>
        <span className="ajuda">{recebivel.descricao}</span>
      </div>
      {podeReceber && (
        escolhendo
          ? (
            <div className="rec-at__meios rec-at__meios--baixa">
              {MEIOS.map((m) => (
                <button key={m.id} type="button" className="rec-at__meio"
                        disabled={salvando} onClick={() => void baixar(m.id)}>{m.rotulo}</button>
              ))}
            </div>
          )
          : <button className="botao botao--pequeno" onClick={() => setEscolhendo(true)}>
              Dar baixa
            </button>
      )}
    </div>
  )
}

/** Erros de dinheiro que merecem frase de gente. */
function mensagemErro(err: unknown, meio: string): string {
  if (err instanceof ErroApi) {
    // Baixa em espécie exige caixa aberto (0028).
    if (meio === 'DINHEIRO' && (err.codigo === 'INVALIDO' || err.status === 409)) {
      return 'Para receber em dinheiro, abra o caixa primeiro (O dinheiro › Caixa).'
    }
    return err.message
  }
  return 'Não foi possível concluir o recebimento.'
}
